// components/ScheduleList.jsx
import React from 'react';
import { Card, Button, Typography, Tag, Space, Modal } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  BellOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { getDaysFromRepeatDays, SCHEDULE_TYPES } from '../../Api/scheduleApiService';


const { Title, Text } = Typography;

const ScheduleItem = ({ schedule, onEdit, onDelete }) => {
  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Schedule',
      content: 'Are you sure you want to delete this schedule?',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => onDelete(schedule.id),
    });
  };

  return (
    <Card
      key={schedule.id}
      size="small"
      className="border-l-4"
      style={{
        borderLeftColor: schedule.isActive ? '#52c41a' : '#d9d9d9'
      }}
      actions={[
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => onEdit(schedule)}
          key="edit"
        >
          Edit
        </Button>,
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          key="delete"
        >
          Delete
        </Button>
      ]}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Title level={5} className="!mb-0">
              {schedule.name}
            </Title>
            <Tag color={schedule.isActive ? 'success' : 'default'}>
              {schedule.isActive ? 'Active' : 'Inactive'}
            </Tag>
            <Tag color="blue">
              {SCHEDULE_TYPES[schedule.type] || 'Unknown'}
            </Tag>
          </div>
          
          <Space direction="vertical" size="small">
            <Text type="secondary">
              <ClockCircleOutlined className="mr-1" />
              Repeat Days: {getDaysFromRepeatDays(schedule.repeatDays).join(', ') || 'None'}
            </Text>
            
            {schedule.startDate && (
              <Text type="secondary">
                <CalendarOutlined className="mr-1" />
                Duration: {dayjs(schedule.startDate).format('MMM DD, YYYY')} - {
                  schedule.endDate ? dayjs(schedule.endDate).format('MMM DD, YYYY') : 'Ongoing'
                }
              </Text>
            )}
            
            {schedule.reminderTimes && schedule.reminderTimes.length > 0 && (
              <div>
                <Text type="secondary">
                  <BellOutlined className="mr-1" />
                  Reminders: 
                </Text>
                {schedule.reminderTimes.map((time, index) => (
                  <Tag key={index} color="orange" className="ml-1">
                    {time}
                  </Tag>
                ))}
              </div>
            )}
          </Space>
        </div>
      </div>
    </Card>
  );
};

const ScheduleList = ({ schedules, onEdit, onDelete }) => {
  return (
    <div className="space-y-4">
      {schedules.map((schedule) => (
        <ScheduleItem
          key={schedule.id}
          schedule={schedule}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

ScheduleItem.propTypes = {
  schedule: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    isActive: PropTypes.bool.isRequired,
    repeatDays: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    reminderTimes: PropTypes.array
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

ScheduleList.propTypes = {
  schedules: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default ScheduleList;